/**
 * I parametri d'indirizzo della scheda azienda, letti in un file che non importa la rotta.
 *
 * ⚠️ Sta qui, e non dentro `validateSearch`, per una ragione che è costata: quella funzione
 * vive dentro la rotta, e la rotta tira dentro l'albero delle rotte e mezza applicazione —
 * quindi la lettura dell'indirizzo, che è tre righe di logica pura, non si poteva provare
 * senza montare tutto. Non provandola, ci è passato in mezzo un difetto per tre giorni.
 *
 * Il difetto: la rinomina all'inglese del 18-09-2026 ha tradotto il campo (`guidaConfig` →
 * `guideConfig`) e la navigazione che lo **scrive**, lasciando indietro la lettura. Nessun
 * errore e nessun tipo rosso — TanStack scarta in silenzio i parametri che non riconosce,
 * quindi «Vai alla modalità guidata» navigava e la pagina restava identica.
 *
 * È la regola 14 nel suo caso meno evidente: **l'indirizzo è dato**. La gente lo incolla in
 * chat, lo mette nei preferiti, lo allega a una segnalazione. Un nome che finisce lì non si
 * traduce di passaggio più di quanto si traduca una chiave del database.
 */
import { COMPANY_SECTIONS, type CompanySection } from './companySections';

export interface CompanySearch {
  /** L'azienda scelta nell'albero. Assente = si sta ancora scegliendo. */
  company?: string;
  section?: CompanySection;
  /**
   * La modalità guidata, accesa.
   *
   * ⚠️ Sta nell'indirizzo e non in uno stato locale: chi la accende e poi condivide il link,
   * o ricarica, si ritroverebbe fuori dalla guida senza aver fatto niente per uscirne. E il
   * nome è lungo di proposito — TanStack fonde i parametri di **tutte** le rotte in un tipo
   * solo, quindi un `guida` generico si scontrerebbe col primo altro che nascesse.
   */
  guideConfig?: true;
}

/** Vero per le forme in cui un «sì» può arrivare: dal codice, da un link scritto a mano. */
const isOn = (v: unknown) => v === true || v === 'true' || v === '1';

/**
 * I nomi vecchi delle sezioni, che continuano a portare dove quella sezione vive adesso.
 *
 * ⚠️ È la stessa ragione di `guidaConfig` qui sotto, e la stessa del commento in testa: **un
 * indirizzo è dato**. `?section=configuration` è finito in chat, nei preferiti e nelle
 * segnalazioni; senza questa riga non darebbe errore — TanStack scarta in silenzio quel che non
 * riconosce — e aprirebbe la **prima** sezione della pagina, che è il modo peggiore di rompere un
 * link: sembra che funzioni, e mostra un'altra cosa.
 */
const RENAMED: Record<string, CompanySection> = {
  /* 28-09-2026: «Configurazione» conteneva sette blocchi di Lares, ne è rimasto uno. */
  configuration: 'modules',
};

function readSection(raw: unknown): CompanySection | undefined {
  if (typeof raw !== 'string') return undefined;
  const renamed = RENAMED[raw];
  if (renamed) return renamed;
  return COMPANY_SECTIONS.includes(raw as CompanySection) ? (raw as CompanySection) : undefined;
}

export function parseCompanySearch(s: Record<string, unknown>): CompanySearch {
  return {
    company: typeof s.company === 'string' && s.company ? s.company : undefined,
    section: readSection(s.section),
    /* `guidaConfig` resta accettato per i link usciti fra l'11 e il 18 settembre: costa una
       clausola, e un link che non fa niente è indistinguibile da una funzione rotta. */
    guideConfig: isOn(s.guideConfig) || isOn(s.guidaConfig) ? true : undefined,
  };
}
